import Layout from '@/components/layout/main-layout';
import {Container, Tab, Tabs, useTheme} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import EventView from '@/components/events/events-view';
import GroupView from '@/components/groups/groups-view';
import ModulesView from '@/components/modules/modules-view';
import ScrollToTop from '@/components/ui/scroll-to-top';
import HomeIcon from '@mui/icons-material/Home';
import CompanyDashboard from '@/components/company/dashboard';
import {useAppSelector} from '@/redux-store/store';
import {env} from '@/config';
import {useLocation} from 'react-router-dom';
import {useDispatch} from 'react-redux';
import {
  purgeAppTag,
  setAppTag,
  setCurrentTab,
  setIsPublicCompany
} from '@/redux-store/slices/auth';
import {decodeBase64ToObject, isAllowedTag} from '@/utils';
import {
  EVENTS_TAB_CUSTOM_KEYS,
  SHOULD_START_WITH_EVENT_TAGS
} from '@/constants';

const Home: React.FC = () => {
  const {t} = useTranslation();
  const location = useLocation();
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const excludedSegments = ['home', 'event', 'group', 'on-demand'];
  const tag =
    pathSegments[0] && !excludedSegments.includes(pathSegments[0])
      ? pathSegments[0] || null
      : null;

  const isOnDemand = pathSegments[0] == 'on-demand';
  const isEventPage =
    (pathSegments[0] === 'event' || (tag && pathSegments[1] === 'event')) &&
    pathSegments.length > (tag ? 2 : 1);

  const eventId = isEventPage
    ? pathSegments[tag ? 2 : 1]
    : isOnDemand
    ? pathSegments[1]
    : null;

  const isGroupPage =
    (pathSegments[0] === 'group' || (tag && pathSegments[1] === 'group')) &&
    pathSegments.length > (tag ? 2 : 1);

  const groupId = isGroupPage ? pathSegments[tag ? 2 : 1] : null;
  const {appTag, currentTab, isPublicCompany} = useAppSelector(
    (state) => state.auth
  );
  const userRole = useAppSelector((state) => state.auth.keycloakUser?.userRole);
  const secondDocumentId =
    (isEventPage || isGroupPage) && pathSegments[tag ? 3 : 2];

  const dispatch = useDispatch();

  const modules = useAppSelector((state) => state.modules.modules);

  const {APP_VARIANT, TAG_PAIRS, PUBLIC_COMPANIES} = env;

  const isSjOne = APP_VARIANT === 'sjone';

  const shouldShowModules =
    isSjOne && modules.length > 0 && userRole !== 'TRAINER';

  const [isTagLoaded, setIsTagLoaded] = useState(false);

  const handleTabChange = useCallback(
    (_: React.ChangeEvent<unknown>, newValue: number) => {
      dispatch(setCurrentTab(newValue));
    },
    [dispatch]
  );

  const getStartTab = useCallback(
    (tag: string) => {
      if (isSjOne && SHOULD_START_WITH_EVENT_TAGS.includes(tag)) {
        dispatch(setCurrentTab(1));
      } else {
        dispatch(setCurrentTab(0));
      }
    },
    [dispatch, isSjOne]
  );

  const getEventsTabTitle = useCallback(() => {
    if (appTag && EVENTS_TAB_CUSTOM_KEYS[appTag]) {
      return t(EVENTS_TAB_CUSTOM_KEYS[appTag]);
    }
    return t('events_screen');
  }, [appTag, t]);

  const theme = useTheme();

  useEffect(() => {
    if (
      tag &&
      (isAllowedTag(tag, TAG_PAIRS) ||
        decodeBase64ToObject(PUBLIC_COMPANIES)[tag]) &&
      (!appTag || tag !== appTag)
    ) {
      dispatch(setAppTag(tag));
      getStartTab(tag);
    } else if (!tag && appTag) {
      dispatch(purgeAppTag());
    }
    setIsTagLoaded(true);
  }, [appTag, dispatch, tag, TAG_PAIRS, PUBLIC_COMPANIES, getStartTab]);

  useEffect(() => {
    if (!isTagLoaded) return;

    if (eventId) {
      const newTabValue =
        APP_VARIANT === 'sjone'
          ? shouldShowModules || isPublicCompany
            ? 2
            : 1
          : isOnDemand
          ? 2
          : 0;

      dispatch(setCurrentTab(newTabValue));
      return;
    }

    if (groupId) {
      const newTabValue =
        APP_VARIANT === 'sjone' ? (shouldShowModules ? 3 : 2) : 1;
      dispatch(setCurrentTab(newTabValue));
      return;
    }
  }, [
    APP_VARIANT,
    eventId,
    groupId,
    dispatch,
    shouldShowModules,
    isOnDemand,
    isTagLoaded,
    isPublicCompany
  ]);

  useEffect(() => {
    if (
      isPublicCompany &&
      (!tag || !decodeBase64ToObject(PUBLIC_COMPANIES)[tag])
    ) {
      dispatch(setIsPublicCompany(false));
    }
  }, [PUBLIC_COMPANIES, dispatch, isPublicCompany, tag]);

  return (
    <Layout skipBreadcrumb={tag ? 1 : 0}>
      <Container
        maxWidth={'md'}
        sx={{
          pt: 2,
          marginTop: 1,
          position: 'relative'
        }}
      >
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          centered
          TabIndicatorProps={{
            style: {backgroundColor: theme.palette.text.primary}
          }}
        >
          {isSjOne && (
            <Tab
              icon={
                <HomeIcon
                  sx={{
                    fontSize: {xs: 'small', sm: 'medium', md: 'large'},
                    color: theme.palette.text.primary
                  }}
                />
              }
            />
          )}

          {shouldShowModules && (
            <Tab
              label={
                <ResponsiveTypography
                  sx={{
                    color: theme.palette.text.primary
                  }}
                >
                  {t('info')}
                </ResponsiveTypography>
              }
            />
          )}

          <Tab
            label={
              <ResponsiveTypography
                sx={{
                  color: theme.palette.text.primary
                }}
              >
                {getEventsTabTitle()}
              </ResponsiveTypography>
            }
          />

          <Tab
            label={
              <ResponsiveTypography
                sx={{
                  color: theme.palette.text.primary
                }}
              >
                {t('teams')}
              </ResponsiveTypography>
            }
          />
          {!isSjOne && (
            <Tab
              label={
                <ResponsiveTypography
                  sx={{
                    color: theme.palette.text.primary
                  }}
                >
                  {t('on_demand')}
                </ResponsiveTypography>
              }
            />
          )}
        </Tabs>
        {currentTab === 0 && isSjOne && <CompanyDashboard />}
        {currentTab === 1 && shouldShowModules && <ModulesView />}
        {currentTab === (isSjOne ? (shouldShowModules ? 2 : 1) : 0) &&
          isTagLoaded && (
            <EventView
              eventId={eventId || undefined}
              groupId={secondDocumentId || undefined}
            />
          )}
        {currentTab === (isSjOne ? (shouldShowModules ? 3 : 2) : 1) &&
          isTagLoaded && (
            <GroupView
              groupId={groupId || undefined}
              eventId={secondDocumentId || undefined}
            />
          )}
        {currentTab === 2 && !isSjOne && (
          <EventView eventId={eventId || undefined} onDemand />
        )}
      </Container>
      <ScrollToTop />
    </Layout>
  );
};

export default Home;
